/**
 * API Utilities
 *
 * Utility functions for API operations including:
 * - URL building with query parameters
 * - API client with automatic auth headers and common query params
 */

import {
  getDeviceId,
  getUUID,
  getOrCreateSessionId,
  getAuthToken,
  getPat,
  getJwtToken,
} from '@/shared/utils';

// ============================================
// URL Building
// ============================================

/**
 * Build URL with query parameters
 *
 * @param endpoint - Base endpoint URL (can be absolute or relative)
 * @param params - Optional query parameters as key-value pairs
 * @returns Full URL with query string
 *
 * @example
 * ```typescript
 * buildUrl('https://api.example.com/users', { id: '123', status: 'active' })
 * // Returns: 'https://api.example.com/users?id=123&status=active'
 *
 * buildUrl('/api/users', { id: '123' })
 * // Returns: '/api/users?id=123'
 * ```
 */
export function buildUrl(endpoint: string, params?: Record<string, string>): string {
  if (!params || Object.keys(params).length === 0) {
    return endpoint;
  }

  // Handle both absolute and relative URLs
  // For relative URLs, use window.location.origin as base
  const isRelativeUrl = endpoint.startsWith('/');
  const base = isRelativeUrl ? window.location.origin : undefined;
  const url = new URL(endpoint, base);

  for (const [key, value] of Object.entries(params)) {
    url.searchParams.set(key, value);
  }

  // Return relative path for relative URLs, full URL for absolute
  return isRelativeUrl ? url.pathname + url.search : url.toString();
}

// ============================================
// Common API Wrapper
// ============================================

/**
 * Build common query params for APIs
 * Automatically includes sessionId and userId (uuid)
 *
 * @param additionalParams - Additional query parameters to merge
 * @returns Query params object with sessionId and userId included
 */
export function buildQueryParams(
  additionalParams?: Record<string, string>,
): Record<string, string> {
  const sessionId = getOrCreateSessionId();
  const userId = getUUID();

  return {
    sessionId,
    userId,
    ...additionalParams,
  };
}

/**
 * Build common headers for APIs
 * Automatically includes X-Gommt-Mmt-Auth, X-Gommt-Uuid, X-Device-Id from cookies,
 * and X-Gommt-Pat from URL query param if present
 *
 * @param additionalHeaders - Additional headers to merge
 * @returns Headers object with auth headers included
 */
export function buildHeaders(additionalHeaders?: Record<string, string>): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...additionalHeaders,
  };

  const authToken = getAuthToken();
  if (authToken) {
    headers['X-Gommt-Mmt-Auth'] = authToken;
  }

  const uuid = getUUID();
  if (uuid) {
    headers['X-Gommt-Uuid'] = uuid;
  }

  const deviceId = getDeviceId();
  if (deviceId) {
    headers['X-Device-Id'] = deviceId;
  }

  const pat = getPat();
  if (pat) {
    headers['X-Gommt-Pat'] = pat;
  }

  const jwtToken = getJwtToken();
  if (jwtToken) {
    headers['Authorization'] = `Bearer ${jwtToken}`;
  }

  return headers;
}

/**
 * Request options for API calls
 */
export interface ApiRequestOptions {
  /** HTTP method (default: POST) */
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  /** Request body for POST/PUT requests */
  body?: unknown;
  /** Additional query parameters (sessionId and userId are added automatically) */
  queryParams?: Record<string, string>;
  /** Additional headers (X-Gommt-Mmt-Auth, X-Gommt-Uuid, X-Device-Id, X-Gommt-Pat are added automatically) */
  headers?: Record<string, string>;
  /** Skip adding auth headers (default: false) */
  skipAuth?: boolean;
  /** Skip adding common query params - sessionId and userId (default: false) */
  skipCommonParams?: boolean;
}

/**
 * Generic API error class
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly statusCode?: number,
    public readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Generic API client with automatic auth handling
 *
 * Automatically adds:
 * - Query params: sessionId, userId
 * - Headers: X-Gommt-Mmt-Auth, X-Gommt-Uuid, X-Device-Id, X-Gommt-Pat (if present), Content-Type
 *
 * @param endpoint - API endpoint URL
 * @param options - Request options
 * @returns Parsed JSON response
 *
 * @example
 * ```typescript
 * // Simple POST request
 * const data = await apiFetch<MyResponse>(FLIGHTS_ENDPOINTS.REVIEW, {
 *   queryParams: { crId: '123', rkey: 'abc' },
 * });
 *
 * // POST request with body
 * const result = await apiFetch<MyResponse>(FLIGHTS_ENDPOINTS.PREBOOK, {
 *   method: 'POST',
 *   body: { itineraryId: '123' },
 *   queryParams: { crId: '123' },
 * });
 *
 * // GET request
 * const addons = await apiFetch<AddonsResponse>(FLIGHTS_ENDPOINTS.ADDONS, {
 *   method: 'GET',
 *   queryParams: { crId: '123' },
 * });
 * ```
 */
export async function apiFetch<T>(endpoint: string, options: ApiRequestOptions = {}): Promise<T> {
  const response = await executeApiFetch(endpoint, options);

  if (!response.ok) {
    throw new ApiError(
      `API request failed: ${response.status} ${response.statusText}`,
      response.status,
    );
  }

  return response.json() as Promise<T>;
}

async function executeApiFetch(endpoint: string, options: ApiRequestOptions): Promise<Response> {
  const {
    method = 'POST',
    body,
    queryParams,
    headers: additionalHeaders,
    skipAuth = false,
    skipCommonParams = false,
  } = options;

  const allQueryParams = skipCommonParams ? (queryParams ?? {}) : buildQueryParams(queryParams);
  const url = buildUrl(endpoint, allQueryParams);

  const headers = skipAuth
    ? { 'Content-Type': 'application/json', ...additionalHeaders }
    : buildHeaders(additionalHeaders);

  const fetchOptions: RequestInit = { method, headers };

  if (body && (method === 'POST' || method === 'PUT')) {
    fetchOptions.body = JSON.stringify(body);
  }

  return fetch(url, fetchOptions);
}
